import { useCallback, useEffect, useRef, useState } from 'react';
import {
  checkCatalogueReferences,
  type CatalogueReferenceMatch,
  type CheckCatalogueReferencesOutcome,
} from '@repo/front-end-services';

/**
 * The Launch step's BI reference check (`launch-bi-gap-preview`, R2.5): whether BI's manufacturer
 * hierarchy will match each placed item's catalogue reference, asked before the rocket.
 *
 * - `idle`: nothing to ask — not on Launch, no token yet, or no references at all.
 * - `checking`: a read is in flight.
 * - `ready`: BI answered every reference of the set `key` names. An answer for any other set is
 *   never shown: the hook reports `checking` until the current set's own answer arrives.
 * - `failed`: "we could not find out" — a failed read, a failed token. Never a pass.
 */
export type LaunchBiCheckState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'ready'; key: string; references: readonly CatalogueReferenceMatch[] }
  | { status: 'failed' };

const IDLE: LaunchBiCheckState = { status: 'idle' };
const CHECKING: LaunchBiCheckState = { status: 'checking' };
const FAILED: LaunchBiCheckState = { status: 'failed' };

interface Params {
  middlewareApi: string;
  /**
   * The page's mount-time token, read only as "signed in yet": not here yet is `idle`, and
   * `tokenFailed` is `failed`. It is never sent, because it is never refreshed.
   */
  accessToken: string | null;
  tokenFailed: boolean;
  /** A fresh, bounded token for each read, *Check again* included, so a long session recovers. */
  getToken: () => Promise<string>;
  /** The sorted distinct references to ask about (`biGapGroups`). The read is keyed on this set. */
  references: readonly string[];
  /** True while the Launch step is showing. Reads happen only then; leaving cancels. */
  active: boolean;
}

export interface UseLaunchBiCheck {
  check: LaunchBiCheckState;
  /** True while a *Check again* read is in flight; the state on screen stays as it was. */
  rechecking: boolean;
  /** Read again: *Check again*. */
  recheck: () => void;
}

/**
 * One read each time Launch opens, and again whenever the reference set changes while it is open
 * (a *Put back* of a floor can change it). Follows `useLaunchHours`: every read takes a sequence
 * number, and a newer set, leaving Launch, unmount or *Check again* aborts the older request and
 * drops its answer — only the latest set's answer is ever shown (spec `## Failure States`,
 * Overtaken).
 */
export const useLaunchBiCheck = ({
  middlewareApi,
  accessToken,
  tokenFailed,
  getToken,
  references,
  active,
}: Params): UseLaunchBiCheck => {
  const [check, setCheck] = useState<LaunchBiCheckState>(IDLE);
  const [rechecking, setRechecking] = useState(false);
  const seqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  // The set as a stable key: the same references in the same order is the same read.
  const key = references.join('\u0000');
  const referencesRef = useRef(references);
  referencesRef.current = references;
  // Read through a ref: a caller's inline getter is a new function each render, and depending on
  // it would re-read on every render.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  /** Take ownership of the state: abort whatever is in flight so its answer is dropped. */
  const claim = useCallback(() => {
    seqRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    return seqRef.current;
  }, []);

  const read = useCallback(
    async (recheck: boolean): Promise<void> => {
      const asked = referencesRef.current;
      const askedKey = asked.join('\u0000');
      const seq = claim();
      const controller = new AbortController();
      abortRef.current = controller;
      if (recheck) setRechecking(true);
      else setCheck(CHECKING);
      // A token that cannot be had is failed. The client never throws by contract; a throw anyway
      // (a broken mock, or a stale package build without this export) is failed too, never an
      // endless `checking`.
      let outcome: CheckCatalogueReferencesOutcome;
      try {
        const token = await getTokenRef.current();
        // Overtaken while the token was fetched: ask nothing.
        if (seq !== seqRef.current) return;
        outcome = await checkCatalogueReferences(middlewareApi, token, asked, controller.signal);
      } catch {
        outcome = { status: 'failed' };
      }
      if (seq !== seqRef.current) return;
      abortRef.current = null;
      setRechecking(false);
      setCheck(
        outcome?.status === 'ready'
          ? { status: 'ready', key: askedKey, references: outcome.references }
          : FAILED,
      );
    },
    [middlewareApi, claim],
  );

  useEffect(() => {
    claim();
    setRechecking(false);
    if (!active || key === '') {
      setCheck(IDLE);
      return;
    }
    if (tokenFailed) {
      setCheck(FAILED);
      return;
    }
    if (!accessToken) {
      setCheck(IDLE);
      return;
    }
    void read(false);
  }, [active, key, accessToken, tokenFailed, read, claim]);

  useEffect(
    () => () => {
      claim();
    },
    [claim],
  );

  // Allowed after a failed mount-time token too: the read fetches its own.
  const recheck = useCallback(() => {
    if (!active || key === '') return;
    void read(true);
  }, [active, key, read]);

  // The set can change in a render before the effect above re-reads (a *Put back*): an answer for
  // the old set, read against the new one, would leave a new reference in no group and could show
  // a clean tick BI was never asked about. So it is never shown as an answer (spec `## Failure
  // States`, Overtaken: only the latest set's answer is shown).
  const shown = check.status === 'ready' && check.key !== key ? CHECKING : check;
  return { check: shown, rechecking, recheck };
};
